import { extractSelection, resolveCondition } from '../parse-market.utils';
import { GameContext, InPlay, PeriodScoresResult, ScopeCompletedResolver } from '../../interfaces';
import { GameCategory, MarketOutcome, PeriodType } from '../../enums';

export const periodTextMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Game]: '',
	[PeriodType.Half1]: ' in the 1st half',
	[PeriodType.Half2]: ' in the 2nd half',
	[PeriodType.Period1]: ' in the 1st quarter',
	[PeriodType.Period2]: ' in the 2nd quarter',
	[PeriodType.Period3]: ' in the 3rd quarter',
	[PeriodType.Period4]: ' in the 4th quarter',
	[PeriodType.Period5]: ' in the 5th quarter',
};

export const nbaScopeResolver: ScopeCompletedResolver = (period, { isCompleted, inPlay }) => {
	if (isCompleted) return true;

	const currentPeriod = Number(inPlay?.period ?? 0);
	const clock = inPlay?.clock ?? null;

	if (!currentPeriod) return false;

	let endPeriod = 0;

	switch (period) {
		case PeriodType.Game:
			return isCompleted;
		case PeriodType.Half1:
			endPeriod = 2;
			break;
		case PeriodType.Half2:
			endPeriod = 4;
			break;
		case PeriodType.Period1:
			endPeriod = 1;
			break;
		case PeriodType.Period2:
			endPeriod = 2;
			break;
		case PeriodType.Period3:
			endPeriod = 3;
			break;
		case PeriodType.Period4:
			endPeriod = 4;
			break;
	}

	if (currentPeriod > endPeriod) return true;

	if (currentPeriod === endPeriod) {
		return clock === '00:00' || clock === null;
	}

	return false;
};

export const ncaabScopeResolver: ScopeCompletedResolver = (period, { isCompleted, inPlay }) => {
	if (isCompleted) return true;

	const currentPeriod = Number(inPlay?.period ?? 0);
	const clock = inPlay?.clock ?? null;

	if (!currentPeriod) return false;

	let endPeriod = 0;

	switch (period) {
		case PeriodType.Game:
			return isCompleted;
		case PeriodType.Half1:
			endPeriod = 1;
			break;
		case PeriodType.Half2:
			endPeriod = 2;
			break;
		case PeriodType.Period1:
			endPeriod = 1;
			break;
		case PeriodType.Period2:
			endPeriod = 2;
			break;
	}

	if (currentPeriod > endPeriod) return true;

	if (currentPeriod === endPeriod) {
		return clock === '00:00' || clock === null;
	}

	return false;
};

export const buildSpreadQuestion = (
	selection: string,
	points: number,
	period: PeriodType
): string => {
	const pts = Math.abs(points);
	const suffix = periodTextMap[period];

	if (points < 0) return `Will ${selection} win by more than ${pts} points${suffix}?`;

	return `Will ${selection} avoid losing by more than ${pts} points${suffix}?`;
};

export const buildTotalPointsQuestion = (points: number, period: PeriodType): string => {
	const pts = Math.abs(points);
	const suffix = periodTextMap[period];

	return `Will the teams combine to score at least ${pts} points${suffix}?`;
};

export const buildTotalPointsOddEvenQuestion = (period: PeriodType): string => {
	const suffix = periodTextMap[period];

	return `Will the total combined points ${suffix} be even?`;
};

export const buildTeamTotalQuestion = (
	team: string,
	points: number,
	period: PeriodType
): string => {
	const pts = Math.abs(points);
	const suffix = periodTextMap[period];

	return `Will ${team} score at least ${pts} points${suffix}?`;
};

export const buildRaceToQuestion = (
	selection: string,
	points: number,
	period: PeriodType
): string => {
	const suffix = periodTextMap[period];

	if (selection === 'Neither') {
		return `Will neither team reach ${points} points first${suffix}?`;
	}

	return `Will ${selection} reach ${points} points first${suffix}?`;
};

export const getPeriodScores = (gc: GameContext, period: PeriodType): PeriodScoresResult => {
	switch (period) {
		case PeriodType.Game:
			return {
				home: gc.homeScore,
				away: gc.awayScore,
				isCompleted: gc.isCompleted,
			};

		case PeriodType.Half1:
		case PeriodType.Half2: {
			const isNcaab = gc.category === GameCategory.NCAAB;
			const periods =
				period === PeriodType.Half1
					? isNcaab
						? ['period_1']
						: ['period_1', 'period_2']
					: isNcaab
						? ['period_2']
						: ['period_3', 'period_4'];

			const home = periods.reduce((sum, p) => sum + (gc.periodScores.home[p] ?? 0), 0);

			const away = periods.reduce((sum, p) => sum + (gc.periodScores.away[p] ?? 0), 0);

			return {
				home,
				away,
				isCompleted: gc.isScopeCompleted(period),
			};
		}

		case PeriodType.Period1:
		case PeriodType.Period2:
		case PeriodType.Period3:
		case PeriodType.Period4:
		case PeriodType.Period5:
			return {
				home: gc.periodScores.home[period] ?? 0,
				away: gc.periodScores.away[period] ?? 0,
				isCompleted: gc.isScopeCompleted(period),
			};

		default:
			return { home: 0, away: 0, isCompleted: false };
	}
};

export const resolveRaceTo = (
	q: string,
	gc: GameContext,
	period: PeriodType,
	target: number
): [MarketOutcome, number] => {
	const isNeither = q.includes('neither team');
	const team = isNeither ? null : extractSelection(q, 'Will ', ['reach']);
	const { home, away, isCompleted } = getPeriodScores(gc, period);

	const isHome = team === gc.homeTeam;
	const isAway = team === gc.awayTeam;

	const selectedTotal = isNeither ? 0 : isHome ? home : away;

	if (home >= target && away < target) {
		if (isNeither) return [MarketOutcome.Lost, selectedTotal];

		return [resolveCondition(isHome, true), selectedTotal];
	}

	if (away >= target && home < target) {
		if (isNeither) return [MarketOutcome.Lost, selectedTotal];

		return [resolveCondition(isAway, true), selectedTotal];
	}

	if (isCompleted) {
		return [resolveCondition(isNeither, true), selectedTotal];
	}

	return [MarketOutcome.Pending, selectedTotal];
};

export const isFirst3MinutesCompleted = (result: any): boolean => {
	const inPlay = result.score?.in_play;

	if (!inPlay) return false;

	if (Number(inPlay.period) > 1) return true;

	if (result.status === 'completed') return true;

	if (Number(inPlay.period) !== 1) return false;

	const clock = inPlay.clock;

	if (!clock) return false;

	const [min, sec] = clock.split(':').map(Number);
	const totalSeconds = min * 60 + sec;

	// 9:00 = 540 seconds
	return totalSeconds <= 540;
};

export const calculateTimeProgress = (
	inPlay: InPlay,
	{
		periodMinutes,
		regulationPeriods,
		overtimeMinutes = 5,
	}: { periodMinutes: number; regulationPeriods: number; overtimeMinutes?: number }
): number | null => {
	const period = Number(inPlay?.period);
	const clock = inPlay?.clock;
	if (!period || !clock) return null;

	const [min, sec] = clock.split(':').map(Number);
	const elapsedInCurrent =
		(period <= regulationPeriods ? periodMinutes : overtimeMinutes) - (min + sec / 60);

	const otPeriods = Math.max(0, period - regulationPeriods);

	// Only full completed periods, not including current
	const fullPeriodsElapsed = Math.min(period - 1, regulationPeriods) * periodMinutes;
	const fullOtElapsed = Math.max(0, otPeriods - 1) * overtimeMinutes;

	const elapsed = fullPeriodsElapsed + fullOtElapsed + elapsedInCurrent;
	const total = regulationPeriods * periodMinutes + otPeriods * overtimeMinutes;

	return elapsed / total;
};

export const nbaTimeProgressResolver = (inPlay: InPlay) =>
	calculateTimeProgress(inPlay, { periodMinutes: 12, regulationPeriods: 4, overtimeMinutes: 5 });

export const ncaabTimeProgressResolver = (inPlay: InPlay) =>
	calculateTimeProgress(inPlay, { periodMinutes: 20, regulationPeriods: 2, overtimeMinutes: 5 });
